import { Trans, useLingui } from '@lingui/react'
import { type FC, useEffect, useState } from 'react'
import { useSelector } from 'react-redux'

import { REDPOINTS_SUBSCRIPTION_STATUS_SUBSCRIBED } from 'shared/consts/redpointsSubscription'
import { type IState } from 'types/redux/store'
import Notification, {
  NOTIFICATION_TYPES,
} from 'views/components/molecules/Notification'
import { isExpandedByDefaultForTenant } from 'views/components/organisms/Cart/helper/isExpandedByDefaultForTenant'
import RedeemRedPoints from 'views/components/organisms/Cart/RedeemRedPoints'
import {
  hasERxSelector,
  hasSAEGroupSelector,
  isMixedGroupSelector,
} from 'views/components/organisms/Cart/selectors'
import Collapsible from 'views/components/organisms/Collapsible'
import useShopAndRuntimeConfig from 'views/enhancers/useShopAndRuntimeConfig'

import LoginButton from './LoginButton'

type Props = {
  position?: string
}

const RedPointsCollapsible: FC<Props> = ({ position = 'last' }) => {
  const { i18n } = useLingui()
  const {
    publicRuntimeConfig: {
      publicConfig: { tenant },
    },
  } = useShopAndRuntimeConfig()

  const loggedInUser = useSelector(
    (state: IState) => state.userSession.newLogin
  )

  const hasRedpointsSubscription = useSelector(
    (state: IState) =>
      state.cart.data.redPointsRegistrationStatus ===
      REDPOINTS_SUBSCRIPTION_STATUS_SUBSCRIBED
  )

  const redPointsPossibleDiscounts = useSelector(
    (state: IState) => state.cart.data.redPointsPossibleDiscounts
  )

  const redeemableRedPoints = useSelector(
    (state: IState) => state.cart.data.redeemableRedPoints
  )

  const hasSAEGroup = useSelector(hasSAEGroupSelector)
  const isMixedGroup = useSelector(isMixedGroupSelector)
  const hasERx = useSelector(hasERxSelector)

  /**
   * isOpenRedPointsBox is used to close the RedeemRedpointsBox on initial render, when
   * there are no possible discounts returned from the API...
   * This does NOT work nicely with the expander in the case when a user has possible discounts,
   * removes items from his/her cart and as a result has no more possible discounts.
   * In this case the RedeemRedpointsBox should stay open. We handle this via the useEffect.
   * One example would be to have only item in the cart left which costs less then one euro,
   * because the minimum of RedPoints which can be redeemed is 1.000.
   */
  const [isOpenRedPointsBox, setIsOpenRedPointsBox] = useState(
    redPointsPossibleDiscounts.length > 0
  )

  useEffect(() => {
    if (redPointsPossibleDiscounts.length > 0 && !isOpenRedPointsBox) {
      setIsOpenRedPointsBox(true)
    }
  }, [redPointsPossibleDiscounts, isOpenRedPointsBox])

  const isExpandedByDefault =
    isExpandedByDefaultForTenant(tenant) &&
    loggedInUser &&
    hasRedpointsSubscription &&
    isOpenRedPointsBox &&
    hasSAEGroup
  const [isExpanded, setIsExpanded] = useState(isExpandedByDefault)

  useEffect(() => {
    if (!hasSAEGroup) {
      setIsExpanded(false)
    }
  }, [hasSAEGroup])

  return (
    <Collapsible
      id="redpoints-collapsible"
      label={i18n._('cart.button.label.redpoints')}
      position={position}
      allScreensizes
      light
      forceDisplayWhenCollapsed
      expanded={isExpanded}
      disabled={!hasSAEGroup}
      displayOverflow
      handleClick={() => {
        setIsExpanded(!isExpanded)
      }}
    >
      {isExpanded && loggedInUser && hasRedpointsSubscription && (
        <RedeemRedPoints
          redPointsPossibleDiscounts={redPointsPossibleDiscounts}
          redPointsInCustomerAccount={redeemableRedPoints}
          hasSAEGroup={hasSAEGroup}
          isMixedGroup={isMixedGroup}
          hasERx={hasERx}
        />
      )}
      {isExpanded && loggedInUser && !hasRedpointsSubscription && (
        <div>
          <div className="u-margin--bottom">
            <Notification
              label={i18n._(
                'cart.loggedIn.noRedPointsUser.redPointsCustomerProgramm.infoBox'
              )}
              type={NOTIFICATION_TYPES.INFO}
            />
          </div>
          <span className="u-margin-xx-small">
            <Trans id="cart.loggedIn.noRedPointsUser.redPointsCustomerProgramm.subText" />
          </span>
        </div>
      )}
      {!loggedInUser && <LoginButton />}
    </Collapsible>
  )
}

export default RedPointsCollapsible
